import { TextField, Typography } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { ConfiguratorContext } from "../../../../../../contexts/ConfiguratorContext";
import { FlexContainer } from "../../../../../../shared/components/styled/Container.styled";
import {
  NOTIFICATIONS_EMAIL_DOCUMENT_UPLOAD_KEY,
  NOTIFICATIONS_EMAIL_DOCUMENT_UPLOAD_LABEL,
} from "../../../../../../shared/constants/Configurator.constants";
import { ERROR_EMAIL_INVALID } from "../../../../../../shared/constants/Login.constants";
import { useDebouncedEffect } from "../../../../../../shared/hooks/useDebouncedEffect";
import { useIsMount } from "../../../../../../shared/hooks/useIsMount";
import { validateEmail } from "../../../../../../shared/utils/utility-functions";

/** The single address notified whenever a borrower uploads a document. */
const DocumentUploadNotificationEmail = () => {
  const isFirstRender = useIsMount();
  const { edittedFields, addNewEdittedField, setValidationError } =
    useContext(ConfiguratorContext);

  const [email, setEmail] = useState(
    edittedFields.find(
      (item) => item.key === NOTIFICATIONS_EMAIL_DOCUMENT_UPLOAD_KEY,
    )?.value || "",
  );
  const [errorMessage, setErrorMessage] = useState("");

  // An address left empty is allowed: nobody is then emailed about an upload. The message is only
  // shown once the admin leaves the field, but publishing is withheld from the first malformed
  // character.
  const hasInvalidEmail =
    email.trim() !== "" && !validateEmail(email.trim());

  useDebouncedEffect(
    () => {
      if (!isFirstRender && !hasInvalidEmail) {
        addNewEdittedField({
          key: NOTIFICATIONS_EMAIL_DOCUMENT_UPLOAD_KEY,
          value: email.trim(),
          modified: true,
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [email, hasInvalidEmail],
    200,
  );

  useEffect(() => {
    setValidationError(NOTIFICATIONS_EMAIL_DOCUMENT_UPLOAD_KEY, hasInvalidEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasInvalidEmail]);

  // A malformed address must not keep the publish action disabled once the admin has navigated
  // away from this setting, or once the borrower is no longer allowed to upload at all.
  useEffect(
    () => () => setValidationError(NOTIFICATIONS_EMAIL_DOCUMENT_UPLOAD_KEY, false),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <FlexContainer
      width="100%"
      gap="25px"
      justifycontent="space-between"
      padding="0 25px 0 0"
    >
      <Typography fontWeight="bold" fontSize={16} sx={{ flexGrow: 3 }}>
        {NOTIFICATIONS_EMAIL_DOCUMENT_UPLOAD_LABEL}
      </Typography>
      <TextField
        variant="standard"
        label="Document uploads email"
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          setErrorMessage("");
        }}
        onBlur={() => setErrorMessage(hasInvalidEmail ? ERROR_EMAIL_INVALID : "")}
        error={!!errorMessage}
        helperText={errorMessage}
      ></TextField>
    </FlexContainer>
  );
};

export default DocumentUploadNotificationEmail;
